// 左側面板的〔檢視〕〔群組〕分頁（SPEC #org-tree-ui 第 1、4 點、#org-tree-views、o03 / o03b / o04 / o16 / o17）。
//   - 頁首一行「檢視只是整理方式，不會讓任何人看到他原本沒有權限的設備」
//   - 點檢視 / 群組 = 範圍切到它（伺服器 resolve，逐人過濾）；選中的檢視下面列資料夾與項目（各自的數字不去重；總數以設備去重）
//   - 看不到的參照不出現、不回數量；全空的資料夾不顯示（伺服器就不回）
//   - 群組〔⋯〕：〔通知我這個群組〕、〔在影像牆開啟〕；批次作業到「設備」頁（按鈕依所選設備 actions 的聯集決定畫不畫，DP12）
//   - 檢視、版面、群組的分工（SPEC LIVE「檢視、版面、群組」，第一輪 P2-4、第二輪 R2-P1-8）：「檢視」分頁同一個清單列出
//     檢視與存過的版面（版面 = 沒有資料夾、只有格子的檢視），影像牆〔另存版面〕存進來、「版面…」選單讀同一份；
//     點版面 = 開到影像牆並載入。空狀態寫用途（檢視、群組各一句）。
import { useRef, useState } from 'react';
import { useDismiss } from '../../components/useDismiss';
import { useServerLayouts } from '../live/LayoutMenu';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useScope } from '../../lib/scope';
import { meTreeApi, type DeviceGroup, type View } from '../../lib/orgTreeApi';
import { canEdit } from './AddToDialog';
import { useViewsAndGroups } from './hooks';
import { ViewEditor } from './ViewEditor';
import { DND_SCOPE } from './dnd';

export function ViewsTab({ onPicked }: { onPicked?(): void }) {
  const { t } = useTranslation();
  const { planes, orgs, scope, setScope } = useScope();
  const team = orgs.filter((o) => o.kind === 'team');
  const { views } = useViewsAndGroups(planes, team);
  const [edit, setEdit] = useState<View | 'new' | null>(null);
  const sel = scope.startsWith('view:') ? scope.slice(5) : '';
  const resolved = useQuery({ queryKey: ['me-tree', 'resolve-view', sel], queryFn: () => meTreeApi.resolveView(planes[0], sel), enabled: !!sel && planes.length > 0, retry: false });
  const { layouts, loading: layoutsLoading } = useServerLayouts();
  const nav = useNavigate();
  const orgName = (id?: string) => orgs.find((o) => o.org_id === id)?.display_name ?? '';
  const empty = !views.isLoading && !layoutsLoading && (views.data?.length ?? 0) === 0 && layouts.length === 0;
  return (
    <div className="ot-views" data-testid="views-tab">
      <div className="row">
        <button className="btn btn-sm" onClick={() => setEdit('new')}>
          {t('ot.view_new')}
        </button>
        {/* 「不會讓人看到沒權限的設備」是給管理員的說明，收進 ⓘ（同群組分頁） */}
        <span className="info-tip muted" title={t('ot.views_hint')} aria-label={t('ot.views_hint')} role="note" tabIndex={0}>
          ⓘ
        </span>
      </div>
      {(views.isLoading || layoutsLoading) && <p className="muted small">{t('common.loading')}</p>}
      {empty && (
        <div className="ot-empty" data-testid="views-empty">
          <EmptyArt kind="views" />
          <p className="muted small">{t('ot.views_empty')}</p>
        </div>
      )}
      <ul className="ot-tree" role="tree">
        {(views.data ?? []).map((v) => (
          <li key={v.view_id} role="treeitem" aria-selected={sel === v.view_id}>
            <div className={'ot-row' + (sel === v.view_id ? ' sel' : '')}>
              <span className="ot-twisty">{sel === v.view_id ? '▾' : '▸'}</span>
              <button
                className="ot-name"
                draggable
                onDragStart={(e) => e.dataTransfer.setData(DND_SCOPE, JSON.stringify({ key: 'view:' + v.view_id, label: v.name }))}
                onClick={() => (setScope('view:' + v.view_id, t('ot.view_label', { name: v.name })), onPicked?.())}
              >
                {v.name}
              </button>
              <span className="muted small">{v.owner_kind === 'org' ? (v.owner_name ?? t('ot.org_owned')) : t('ot.personal')}</span>
              <span className="spacer" />
              {sel === v.view_id && resolved.data && (
                <span className="ot-count" title={t('ot.count_tip')}>
                  {resolved.data.device_total}
                </span>
              )}
              {canEdit(v, orgs) && (
                <button className="icon-btn" aria-label={t('ot.edit_named', { name: v.name })} onClick={() => setEdit(v)}>
                  ✎
                </button>
              )}
            </div>
            {sel === v.view_id && resolved.data && <ResolvedTree r={resolved.data} />}
          </li>
        ))}
        {layouts.map((l) => (
          <li key={'layout:' + l.layout_id} role="treeitem" aria-selected={false} data-layout={l.name}>
            <div className="ot-row">
              <span className="ot-twisty" />
              <button className="ot-name" title={t('ot.layout_open_tip')} onClick={() => (nav('/live', { state: { loadLayout: l.layout_id } }), onPicked?.())}>
                ▦ {l.name}
              </button>
              <span className="muted small">
                {t('ot.layout_cells', { count: l.cells.filter(Boolean).length })} · {l.scope === 'org' ? orgName(l.owner_org_id) || t('ot.org_owned') : t('ot.personal')}
              </span>
            </div>
          </li>
        ))}
      </ul>
      {edit && <ViewEditor kind="view" initial={edit === 'new' ? null : edit} close={() => setEdit(null)} />}
    </div>
  );
}

function ResolvedTree({ r }: { r: NonNullable<Awaited<ReturnType<typeof meTreeApi.resolveView>>> }) {
  const { t } = useTranslation();
  const walk = (parent: string | null, depth: number): JSX.Element[] => [
    ...r.folders
      .filter((f) => (f.parent_item_id ?? null) === parent)
      .flatMap((f) => [
        <li key={f.item_id} className="ot-row" style={{ paddingLeft: depth * 14 }} data-folder={f.name}>
          <span className="ot-twisty">▾</span>📁 {f.name}
          <span className="spacer" />
          <span className="ot-count">{f.device_count}</span>
        </li>,
        ...walk(f.item_id, depth + 1),
      ]),
    ...r.items
      .filter((i) => (i.parent_item_id ?? null) === parent)
      .map((i) => (
        <li key={i.item_id} className="ot-row" style={{ paddingLeft: depth * 14 }} data-item={i.name}>
          <span className="ot-twisty" />
          {i.kind === 'group' ? t('ot.group_prefix', { name: i.name }) : i.name}
          {i.from_org_name && <span className="ot-dlg-mark small"> ⤳ {i.from_org_name}</span>}
          <span className="spacer" />
          <span className="ot-count">{i.device_count}</span>
        </li>
      )),
  ];
  return (
    <ul className="plain-list ot-resolved" data-testid="view-resolved">
      {walk(null, 1)}
      {r.unavailable_count ? <li className="muted small">{t('ot.unavailable_n', { count: r.unavailable_count })}</li> : null}
    </ul>
  );
}

export function GroupsTab({ onPicked }: { onPicked?(): void }) {
  const { t } = useTranslation();
  const { planes, orgs, scope, setScope, visibleDevices } = useScope();
  const qc = useQueryClient();
  const nav = useNavigate();
  const team = orgs.filter((o) => o.kind === 'team');
  const { groups } = useViewsAndGroups(planes, team);
  const subs = useQuery({ queryKey: ['me-tree', 'subs', planes[0]?.api], queryFn: () => meTreeApi.subscriptions(planes[0]), enabled: planes.length > 0, retry: false });
  const [edit, setEdit] = useState<DeviceGroup | 'new' | null>(null);
  const [menu, setMenu] = useState('');
  // 群組〔⋯〕選單：Esc、點外面、切範圍都關（全站選單同一套，第二輪 B2-03）
  const menuRef = useRef<HTMLSpanElement>(null);
  useDismiss(menu !== '', () => setMenu(''), menuRef, [scope]);
  const sel = scope.startsWith('group:') ? scope.slice(6) : '';
  // 批次作業：所選群組裡任一台有維運 / 控制才畫入口（UX-11；設備頁再依每台 actions 決定畫哪些鈕）
  const batchable = !!sel && visibleDevices.some((d) => d.actions.some((a) => a.action === 'device.maintain' || a.action === 'device.control'));
  async function toggleSub(g: DeviceGroup) {
    const on = (subs.data?.groups ?? []).some((s) => s.group_id === g.group_id);
    if (on) await meTreeApi.unsubscribe(planes[0], { group_id: g.group_id });
    else await meTreeApi.subscribe(planes[0], { group_id: g.group_id });
    void qc.invalidateQueries({ queryKey: ['me-tree', 'subs'] });
  }
  return (
    <div className="ot-views" data-testid="groups-tab">
      {/* 「群組不授予任何權限」是給管理員的說明，收進 ⓘ、不常駐（LIVE-01 P1-2） */}
      <div className="row">
        <button className="btn btn-sm" onClick={() => setEdit('new')}>
          {t('ot.group_dev_new')}
        </button>
        <span className="info-tip muted" title={t('ot.groups_hint')} aria-label={t('ot.groups_hint')} role="note" tabIndex={0} data-testid="groups-hint">
          ⓘ
        </span>
      </div>
      {groups.data && groups.data.length === 0 && (
        <div className="ot-empty" data-testid="groups-empty">
          <EmptyArt kind="groups" />
          <p className="muted small">{t('ot.groups_empty')}</p>
        </div>
      )}
      <ul className="ot-tree" role="tree">
        {(groups.data ?? []).map((g) => {
          const on = (subs.data?.groups ?? []).some((s) => s.group_id === g.group_id);
          return (
            <li key={g.group_id} role="treeitem" aria-selected={sel === g.group_id}>
              <div className={'ot-row' + (sel === g.group_id ? ' sel' : '')}>
                <span className="ot-twisty" />
                <button
                  className="ot-name"
                  draggable
                  onDragStart={(e) => e.dataTransfer.setData(DND_SCOPE, JSON.stringify({ key: 'group:' + g.group_id, label: g.name }))}
                  onClick={() => (setScope('group:' + g.group_id, t('ot.group_prefix', { name: g.name })), onPicked?.())}
                >
                  ▦ {g.name}
                </button>
                <span className="muted small">{g.owner_kind === 'org' ? (g.owner_name ?? t('ot.org_owned')) : t('ot.personal')}</span>
                <span className="spacer" />
                {sel === g.group_id && <span className="ot-count">{visibleDevices.length}</span>}
                <span className="ot-menu-wrap" ref={menu === g.group_id ? menuRef : undefined}>
                  <button className="icon-btn" aria-label={t('ot.more_actions', { name: g.name })} onClick={() => setMenu(menu === g.group_id ? '' : g.group_id)}>
                    ⋯
                  </button>
                  {menu === g.group_id && (
                    <span className="ot-menu" role="menu">
                      <button role="menuitemcheckbox" aria-checked={on} onClick={() => (setMenu(''), void toggleSub(g))}>
                        {on ? '☑ ' : '☐ '}
                        {t('ot.notify_me_group')}
                      </button>
                      <button role="menuitem" onClick={() => (setMenu(''), setScope('group:' + g.group_id, t('ot.group_prefix', { name: g.name })), nav('/live', { state: { fillScope: true } }))}>
                        {t('ot.open_in_wall')}
                      </button>
                      {canEdit(g, orgs) && (
                        <button role="menuitem" onClick={() => (setMenu(''), setEdit(g))}>
                          {t('common.edit')}
                        </button>
                      )}
                    </span>
                  )}
                </span>
              </div>
            </li>
          );
        })}
      </ul>
      {batchable && (
        <Link className="btn btn-sm" to="/devices" data-testid="group-batch">
          {t('ot.group_batch')}
        </Link>
      )}
      {edit && <ViewEditor kind="group" initial={edit === 'new' ? null : edit} close={() => setEdit(null)} />}
    </div>
  );
}

/**
 * 空狀態的示意圖（第四輪 R4-P1-5）：版面 = 一面 2×2 的牆；群組 = 三台攝影機收成一個名單。純裝飾（aria-hidden），顏色跟主題走。
 */
function EmptyArt({ kind }: { kind: 'views' | 'groups' }) {
  if (kind === 'views')
    return (
      <svg className="ot-empty-art" viewBox="0 0 96 56" aria-hidden width="96" height="56">
        {[0, 1].map((r) => [0, 1].map((c) => <rect key={`${r}${c}`} x={4 + c * 46} y={2 + r * 27} width="42" height="24" rx="3" fill="none" stroke="currentColor" strokeWidth="2" />))}
        <circle cx="25" cy="14" r="4" fill="currentColor" />
        <circle cx="71" cy="41" r="4" fill="currentColor" />
      </svg>
    );
  return (
    <svg className="ot-empty-art" viewBox="0 0 120 56" aria-hidden width="120" height="56">
      {[0, 1, 2].map((i) => (
        <g key={i} transform={`translate(4 ${4 + i * 17})`}>
          <rect width="18" height="12" rx="2" fill="none" stroke="currentColor" strokeWidth="2" />
          <path d="M18 6 l6 -4 v8 z" fill="currentColor" />
        </g>
      ))}
      <path d="M36 10 C56 10 56 28 70 28 M36 27 H70 M36 44 C56 44 56 28 70 28" fill="none" stroke="currentColor" strokeWidth="2" />
      <rect x="72" y="14" width="44" height="28" rx="4" fill="none" stroke="currentColor" strokeWidth="2" />
      {[0, 1, 2].map((i) => (
        <line key={i} x1="80" x2="108" y1={21 + i * 7} y2={21 + i * 7} stroke="currentColor" strokeWidth="2" />
      ))}
    </svg>
  );
}
